import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Truck, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "27 February 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20 mb-10" data-testid={`terms-section-${id}`}>
    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
      {title}
    </h2>
    <div className="text-[15px] text-slate-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Terms of Service — Transport Anything 247</title>
        <meta name="description" content="Terms of service for Transport Anything 247." />
        <link rel="canonical" href="https://transportanything247.com/terms" />
      </Helmet>

      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-7 w-7 text-orange-500" />
            <span className="text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>
              Transport Anything 247
            </span>
          </Link>
          <Link to="/" className="text-xs text-slate-300 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
            Terms of Service
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Last updated: <span className="font-medium text-slate-700">{LAST_UPDATED}</span>
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Section id="introduction" title="1. Introduction">
          <p>
            These Terms of Service ("Terms") form a legally binding agreement between you and
            Transport Anything 247 ("we", "us", "our"). They govern your use of the
            transportanything247.com website, the Transport Anything 247 customer mobile app and
            the Transport 247 Driver mobile app (together, "the Service").
          </p>
          <p>By using the Service, you agree to these Terms and to our <Link to="/privacy" className="text-orange-600 underline">Privacy Policy</Link>.</p>
        </Section>

        <Section id="eligibility" title="2. Eligibility">
          <p>You must be 18 or over to use the Service. By creating an account you confirm that:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>you are 18 or older;</li>
            <li>you have the legal capacity to enter into these Terms;</li>
            <li>you provide accurate, current and complete account information; and</li>
            <li>if you register as a driver, you hold a valid UK driving licence and goods-carriage insurance for every vehicle you use on the platform.</li>
          </ul>
        </Section>

        <Section id="marketplace" title="3. The marketplace model">
          <p>
            Transport Anything 247 is a marketplace. We connect customers seeking transport with
            independent drivers who provide transport services. <strong>We are not a carrier and
            do not own or operate vehicles.</strong> Each booking results in a direct contract for
            the transport of goods between the customer and the assigned driver.
          </p>
          <p>
            We provide the booking, matching, payment, communication and dispute-resolution tools.
            We do not handle goods, and we do not directly insure shipments.
          </p>
        </Section>

        <Section id="pricing-and-payments" title="4. Pricing and payments">
          <p>
            Quotes shown in the Service are calculated from postcode-to-postcode distance, item
            category and selected service speed. Prices include VAT where applicable. Customers
            authorise us, via Stripe, to charge their saved card upon booking confirmation.
          </p>
          <p>
            Drivers receive up to 75% of the customer-paid amount; the platform retains the
            balance to cover marketplace operations, dispatch infrastructure, payment processing
            and customer support. Driver earnings are paid weekly via Stripe Connect to the bank
            account provided during onboarding.
          </p>
        </Section>

        <Section id="cancellations" title="5. Cancellations and refunds">
          <p>
            Customers may cancel a booking up to the point a driver has been assigned. Bookings
            cancelled after driver assignment may incur a partial charge equal to the driver's
            time and travel to that point. Drivers must not cancel an accepted job without
            reasonable cause; repeated cancellations may lead to account suspension.
          </p>
          <p>
            Refunds for service failures (non-delivery, damage attributable to the driver, etc.)
            are handled case-by-case via our support team. Submit a claim within 7 days of the
            delivery date at <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a>.
          </p>
        </Section>

        <Section id="acceptable-use" title="6. Acceptable use">
          <p>
            Use of the Service is also governed by our{" "}
            <Link to="/aup" className="text-orange-600 underline">Acceptable Use Policy</Link>, which
            lists prohibited items, conduct rules and platform-integrity requirements in detail. In
            summary, you agree not to use the Service to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>ship illegal items (controlled substances, weapons, counterfeit goods, etc.);</li>
            <li>ship hazardous items without explicitly selecting the hazardous category and meeting carrier-side requirements;</li>
            <li>impersonate any person, share account credentials or operate multiple accounts to circumvent platform controls;</li>
            <li>interfere with the platform's normal operation (scraping, automated load testing, etc.); or</li>
            <li>misuse the in-app chat (harassment, spam, sharing of unsolicited commercial content).</li>
          </ul>
          <p>We may suspend or terminate accounts that breach these rules.</p>
        </Section>

        <Section id="driver-obligations" title="7. Driver obligations">
          <p>Approved drivers must:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>hold and maintain a valid UK driving licence and goods-carriage insurance;</li>
            <li>operate vehicles in a roadworthy condition;</li>
            <li>follow all applicable road-traffic and goods-vehicle regulations;</li>
            <li>treat customers respectfully and adhere to agreed pickup / delivery times;</li>
            <li>capture accurate proof-of-delivery photos and use the in-app workflow honestly; and</li>
            <li>complete Stripe Connect onboarding to receive payouts — we cannot pay drivers via any other channel.</li>
          </ul>
        </Section>

        <Section id="liability" title="8. Liability">
          <p>
            To the maximum extent permitted by law, our total aggregate liability to you for any
            claim arising from the Service is limited to the amount you paid us in the 12 months
            preceding the claim. We are not liable for indirect, incidental, consequential or
            punitive damages, including lost profits or loss of business opportunity.
          </p>
          <p>
            We do not warrant uninterrupted service, error-free operation, or any specific
            commercial outcome. The Service is provided "as is" subject to the warranties
            required by UK consumer law (which we do not seek to exclude).
          </p>
        </Section>

        <Section id="intellectual-property" title="9. Intellectual property">
          <p>
            The Service, including its branding, copy, design, software and analytics, belongs to
            Transport Anything 247 or its licensors. You may use the Service only as expressly
            permitted by these Terms. You retain rights to content you submit (POD photos, chat
            messages, profile info), and grant us a non-exclusive licence to host and display it
            to the parties involved in the relevant booking.
          </p>
        </Section>

        <Section id="termination" title="10. Termination">
          <p>
            You can close your account at any time from the Profile screen or by emailing
            customers@transportanything247.com. We can suspend or terminate accounts that breach
            these Terms, that pose risk to other users, or that we are required to suspend by
            law. Sections that by their nature survive termination (liability, IP, governing
            law) continue to apply.
          </p>
        </Section>

        <Section id="changes" title="11. Changes to these Terms">
          <p>
            We may update these Terms when the Service changes materially. We will revise the
            "Last updated" date above and, for material changes, notify active users by email.
            Continued use of the Service after the effective date constitutes acceptance of the
            updated Terms.
          </p>
        </Section>

        <Section id="law" title="12. Governing law">
          <p>
            These Terms are governed by the laws of England and Wales. Any dispute will be
            subject to the exclusive jurisdiction of the courts of England and Wales (consumers
            may also bring claims in their UK home jurisdiction).
          </p>
        </Section>

        <Section id="contact" title="13. Contact">
          <div className="bg-slate-900 text-white rounded-xl p-5">
            <p className="text-sm leading-relaxed">
              Questions about these Terms:<br />
              <a href="mailto:customers@transportanything247.com" className="text-orange-400 underline font-semibold">
                customers@transportanything247.com
              </a>
            </p>
          </div>
        </Section>

        <p className="text-xs text-slate-500 mt-12 text-center">
          © {new Date().getFullYear()} Transport Anything 247. All rights reserved.
        </p>
      </main>
    </div>
  );
}
